/**
 * @fileoverview Hook to handle session errors and automatic redirects
 * @module @auth/hooks/useSessionError
 */

'use client';

// === React & React Hooks ===
import { useEffect } from 'react';

// === Next.js ===
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

// === Types & Interfaces ===
import type { AuthEventProps } from '@/lib/features/tracking/types';

// === Utils & Helpers ===
import { studioService } from '@/@studio/services';
import { LocalAPIEndpoints } from '@/@studio/types/constants';
import { shouldRedirectForLicense } from '@/@studio/utils/routeAccess';
import { Routes } from '@/app/routes';
import { EVENTS } from '@/lib/features/tracking/events';
import { tracking } from '@/lib/features/tracking/tracking';
import { UNAUTHENTICATED_STATUS } from '@/types';
import { Session } from 'next-auth';

/**
 * Hook to handle session errors and redirect users appropriately
 *
 * Features:
 * - Monitors session state for errors
 * - Handles token refresh failures
 * - Redirects to sign-in when session expires
 * - Tracks error events for monitoring
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { hasSessionError } = useSessionError();
 *
 *   if (hasSessionError) {
 *     return <div>Session error occurred</div>;
 *   }
 *
 *   return <div>Normal content</div>;
 * }
 * ```
 *
 * @returns Object containing session error state
 */
export function useSessionError(session: Session | null, status: string) {
    // === Hooks ===
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    useEffect(() => {
        if (!session || status === UNAUTHENTICATED_STATUS || session.error) {
            // Bail out if we're anywhere on/under the sign-in route, not just an exact
            // match — a strict `pathname === Routes.SIGNIN` check was confirmed to let a
            // redirect loop compound indefinitely once anything upstream (middleware, the
            // NextAuth `redirect` callback) ever produced a pathname that merely CONTAINS
            // sign-in rather than equaling it exactly (e.g. a stray "/undefined" prefix),
            // since the exact-equality check would then never bail out again.
            if (pathname?.includes(Routes.SIGNIN)) {
                // If we're already on/under the sign-in page, don't redirect again
                return;
            }

            // Use the full current path including query parameters as the callback
            const currentUrl =
                pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
            router.replace(`${Routes.SIGNIN}?callbackUrl=${encodeURIComponent(currentUrl)}`);
        }
    }, [session, status, router, pathname, searchParams]);
}

export function useExpiredLicense(session: Session | null, pathname: string | null) {
    // === Hooks ===
    const router = useRouter();

    useEffect(() => {
        // Skip if no session, no organization, or no pathname
        if (!session || !session.org || !pathname) return;

        const shouldRedirect = shouldRedirectForLicense(pathname, {
            user: {
                isInternal: session.user?.isInternal,
                hasStudioAIAccess: session.user?.hasStudioAIAccess,
                isUnaffiliated: session.user?.isUnaffiliated,
            },
            org: session.org,
        });

        if (shouldRedirect) {
            // Track expired license event
            tracking.event<AuthEventProps>(EVENTS.AUTH.LICENSE_EXPIRED, {
                orgId: session.org.orgId,
                redirectTo: Routes.HOME,
            });

            // Redirect to home page (not billing - user can navigate to billing from there)
            router.push(Routes.HOME);
        }
    }, [session, pathname, router]);
}

/**
 * Sets a demo token cookie for internal users if not already set.
 * The cookie is signed with the user's email and expires in 8 hours.
 */
export function useDemoCookie(session: Session | null) {
    useEffect(() => {
        async function setDemoCookie() {
            if (
                session?.user?.isInternal &&
                typeof window !== 'undefined' &&
                !document.cookie.includes('studio.demo-token')
            ) {
                const jwtData = { email: session.user.email };
                const res = await fetch(LocalAPIEndpoints.ENCRYPT_BUILDER, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ jwtData, expiresIn: '8h' }),
                });
                if (!res.ok) {
                    tracking.errorWithSource(
                        new Error(`Failed to fetch demo token. Status: ${res.status}`),
                        'auth.useDemoCookie.sign',
                        { status: res.status },
                    );
                    return;
                }
                const { token } = await res.json();

                const expires = new Date();
                expires.setTime(expires.getTime() + 8 * 60 * 60 * 1000); // 8 hours
                const domain =
                    process.env.NODE_ENV === 'development' ? 'localhost' : '.mfourstudio.com';

                document.cookie =
                    `studio.demo-token=${token}; ` +
                    `expires=${expires.toUTCString()}; ` +
                    `domain=${domain}; ` +
                    `path=/; ` +
                    `SameSite=Lax`;
            }
        }
        setDemoCookie();
    }, [session]);
}

/**
 * Records a page visit, fire and forget. The service is async, so a synchronous try/catch never saw
 * its rejection: an unreachable API escaped as an unhandled rejection, which Next.js shows as a
 * Runtime Error overlay on every page in development. The service already reports the failure to
 * tracking, so the rejection is only swallowed here.
 */
export function logRouteVisit(route: string): void {
    studioService.logUserRouteVisit(route).catch(() => undefined);
}
